import React, { useContext } from 'react';
import { StudioContext, StudioSidePanelContext, UserContext } from 'venn-components';
import { Icon } from 'venn-ui-kit';
import { getDynamicIcon } from '../../../../logic/studioUtils';
import ItemActions from './ItemActions';
import { analyticsService, buttonize } from 'venn-utils';
import { useRecoilValue } from 'recoil';
import {
  allBlockIdsState,
  blockContinuationPageCount,
  blockDisplayHeader,
  blockExpandedState,
  type BlockId,
  blockSettings,
  isReportState,
  isSelectedBlockState,
} from 'venn-state';
import { cn } from '@/lib/utils';

interface BlockItemProps {
  id: BlockId;
  showArrowOnHover?: boolean;
  showContinuationLine?: boolean;
}

const BlockItem = ({ id, showArrowOnHover, showContinuationLine }: BlockItemProps) => {
  const { hasPermission } = useContext(UserContext);
  const isReport = useRecoilValue(isReportState);
  const blockSetting = useRecoilValue(blockSettings(id));
  const header = useRecoilValue(blockDisplayHeader(id));
  const isSelected = useRecoilValue(isSelectedBlockState(id));
  const isExpanded = useRecoilValue(blockExpandedState(id));
  const continuationCount = useRecoilValue(blockContinuationPageCount(id));
  const isSpanning = isExpanded && continuationCount > 0;
  const totalPages = isSpanning ? 1 + continuationCount : 0;
  const { onDuplicateBlock, onDeleteBlock } = useContext(StudioContext);
  const { onSelectGlobal, onSelectBlock } = useContext(StudioSidePanelContext);
  const allBlockIds = useRecoilValue(allBlockIdsState);

  const duplicateBlock = () => {
    if (!blockSetting || !hasPermission('STUDIO_INSERT_BLOCKS')) {
      return;
    }

    const index = allBlockIds.findIndex((blockId) => id === blockId);
    onDuplicateBlock(id, index + 1, blockSetting.customBlockType);

    analyticsService.ctaClicked({
      purpose: 'copy studio block',
      locationOnPage: `${blockSetting.customBlockType} block left side bar`,
    });
  };

  const dragArrow = <Icon type="arrows-up-down" className="reorderable-hover text-venn-dark-blue text-[12px]" />;

  return (
    <div
      className={cn(
        'relative flex flex-1 items-center justify-start gap-x-[5px] py-[5px] h-[28px] max-h-[28px] min-w-0 group/item',
        isReport && (!showContinuationLine || (showContinuationLine && isSpanning)) && 'pl-[15px]',
        !isReport && 'pl-1.5',
      )}
    >
      {isSpanning && <LinkedBlockIcon />}
      {isReport && !isSpanning && showContinuationLine && (
        <div>
          <div className="flex items-center justify-center w-[10px] translate-x-0.5 shrink-0">{dragArrow}</div>
          <PageIconContinuationLine />
        </div>
      )}
      <div className="flex items-center flex-1 gap-x-[5px] min-w-0">
        {isReport && (
          <div className="flex items-center justify-center w-[15px] shrink-0">
            {!isSpanning && !showContinuationLine && showArrowOnHover && dragArrow}
          </div>
        )}
        <Icon type={getDynamicIcon(blockSetting)} className="text-venn-black text-base shrink-0" />
        <div
          data-testid="qa-block-item"
          className={cn(
            'flex items-baseline gap-x-1 min-w-0 flex-1 text-[14px] cursor-pointer',
            isSelected && 'font-bold',
          )}
          {...buttonize(() => (isSelected ? onSelectGlobal() : onSelectBlock(id, { scrollIntoView: true })))}
        >
          <span className="truncate min-w-0">{header}</span>
          {totalPages > 0 && <span className="text-venn-grey-60 whitespace-nowrap shrink-0"> (1/{totalPages})</span>}
        </div>
      </div>
      <div className="flex items-center">
        <ItemActions
          onDelete={
            !hasPermission('STUDIO_DELETE_BLOCKS')
              ? undefined
              : () => {
                  onDeleteBlock(id);
                  analyticsService.ctaClicked({
                    purpose: 'delete studio block',
                    locationOnPage: `${blockSetting?.customBlockType} block left side bar`,
                  });
                }
          }
          onDuplicate={!hasPermission('STUDIO_INSERT_BLOCKS') ? undefined : () => duplicateBlock()}
          customDeleteText={`block (${header})`}
        />
        <div className="w-[15px] shrink-0" />
      </div>
    </div>
  );
};

export const LinkedBlockIcon = () => (
  <>
    <PageIconContinuationLine />
    <Icon type="link" className="absolute z-[1] bg-white text-[10px] text-black -translate-x-1/2 left-[24px]" />
  </>
);

export const PageIconContinuationLine = ({
  isBeforeIcon = false,
  isAfterIcon = false,
}: {
  isBeforeIcon?: boolean;
  isAfterIcon?: boolean;
}) => (
  <div
    className={cn(
      'absolute w-px bg-repeat-y left-[24px] [background-image:linear-gradient(to_bottom,black_50%,transparent_50%)] [background-size:1px_4px]',
      isBeforeIcon && 'h-1/2 top-0',
      isAfterIcon && 'h-1/2 bottom-0',
      !isBeforeIcon && !isAfterIcon && 'inset-y-0',
    )}
  />
);

export default BlockItem;
